import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { FileEntity } from './file.entity';
import { LocationEntity } from './location.entity';
import { TripInspectionEntity } from './trip-inspection.entity';
import { TripStopEntity } from './trip-stop.entity';
import { TripEntity } from './trip.entity';

/**
 * Category of a trip photo. Unifies the two legacy photo vocabularies
 * (`trip_stop_photo.photoType` = before/after/damage/inspection/proof and
 * `trip_inspection_photo.photoType` = the truck-corner positions) into one
 * "what is this photo for" axis. The truck-corner / inside distinction now
 * lives on the separate `position` column so this stays a clean category.
 *
 * - `pre_trip_inspection`  — driver pre-trip truck photos (legacy `checkout`)
 * - `post_trip_inspection` — driver post-trip truck photos (legacy `checkin`)
 * - `load_confirmation`    — warehouse + driver load-confirm photos; repeats
 *                            per load via `loadSequence` (PD-13744)
 * - `pre_stop`             — stop-level "before delivery" photos (PD-13780)
 * - `post_stop`            — stop-level "after delivery" photos (PD-13780)
 * - `damage`               — damage documentation (either truck or stop)
 */
export type TripPhotoType =
	| 'pre_trip_inspection'
	| 'post_trip_inspection'
	| 'load_confirmation'
	| 'pre_stop'
	| 'post_stop'
	| 'damage';

/**
 * Sub-type for truck photos that occupy a fixed slot. NULL for stop / load
 * photos that have no positional meaning. `inside` is new (PD-13744) — the
 * driver's interior-of-truck shot added alongside the four corners.
 */
export type TripPhotoPosition =
	| 'front_left'
	| 'front_right'
	| 'rear_left'
	| 'rear_right'
	| 'inside';

/**
 * Unified trip photo record (PD-13780 / PD-13766 / PD-13744).
 *
 * Replaces the two separate tables `trip_stop_photo` and
 * `trip_inspection_photo` — every trip-related photo (truck inspection,
 * warehouse/driver load confirmation, and per-stop before/after delivery
 * photos) now lands here, always referencing the trip and optionally the
 * trip stop / inspection / merchandiser location it belongs to.
 *
 * The old tables are dropped by a separate team-run migration AFTER this
 * entity is in use (the create here is additive — DB-sync makes the table;
 * the destructive drop is deliberately NOT done from app code). See
 * `docs/claude/trip-photo-redesign/plan.md`.
 *
 * Relations are intentionally unidirectional (`@ManyToOne` only, no inverse
 * `@OneToMany` on the parents) so adding this entity does not modify the
 * existing Trip / TripStop / TripInspection entities. The read/write paths
 * are cut over to this table in the accompanying `TripPhotoService`.
 */
@Entity('trip_photo')
@Index('idx_trip_photo_trip', ['tripId'])
@Index('idx_trip_photo_trip_stop', ['tripStopId'])
@Index('idx_trip_photo_trip_inspection', ['tripInspectionId'])
@Index('idx_trip_photo_file', ['fileId'])
export class TripPhotoEntity extends BaseEntity {
	@Column({ name: 'fk_trip__id', type: 'int', unsigned: true })
	public tripId!: number;

	@Column({
		name: 'fk_trip_stop__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public tripStopId?: number | null;

	@Column({
		name: 'fk_trip_inspection__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public tripInspectionId?: number | null;

	@Column({
		name: 'fk_location__id_merchandiser',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public merchandiserLocationId?: number | null;

	@Column({
		name: 'fk_file__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public fileId?: number | null;

	@Column({ name: 'imageUrl', type: 'varchar', length: 255, nullable: true })
	public imageUrl?: string | null;

	@Column({
		name: 'thumbnailUrl',
		type: 'varchar',
		length: 255,
		nullable: true,
	})
	public thumbnailUrl?: string | null;

	@Column({ name: 'photoType', type: 'varchar', length: 50 })
	public photoType!: TripPhotoType;

	@Column({ name: 'position', type: 'varchar', length: 50, nullable: true })
	public position?: TripPhotoPosition | null;

	/**
	 * Load-confirmation sequence: 1 = initial load, 2+ = each subsequent
	 * reload during the trip. NULL for non-load photos. Lets the trip carry
	 * a distinct warehouse + driver photo set per load cycle (PD-13744).
	 */
	@Column({
		name: 'loadSequence',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public loadSequence?: number | null;

	@Column({ name: 'notes', type: 'text', nullable: true })
	public notes?: string | null;

	@Column({
		name: 'latitude',
		type: 'decimal',
		precision: 10,
		scale: 8,
		nullable: true,
	})
	public latitude?: number | null;

	@Column({
		name: 'longitude',
		type: 'decimal',
		precision: 11,
		scale: 8,
		nullable: true,
	})
	public longitude?: number | null;

	@Column({ name: 'takenAt', type: 'datetime' })
	public takenAt!: Date;

	@Column({
		name: 'sortOrder',
		type: 'int',
		unsigned: true,
		default: 0,
	})
	public sortOrder!: number;

	@ManyToOne(() => TripEntity)
	@JoinColumn({ name: 'fk_trip__id' })
	public fkTrip?: TripEntity;

	@ManyToOne(() => TripStopEntity, { nullable: true })
	@JoinColumn({ name: 'fk_trip_stop__id' })
	public fkTripStop?: TripStopEntity;

	@ManyToOne(() => TripInspectionEntity, { nullable: true })
	@JoinColumn({ name: 'fk_trip_inspection__id' })
	public fkTripInspection?: TripInspectionEntity;

	@ManyToOne(() => LocationEntity, { nullable: true })
	@JoinColumn({ name: 'fk_location__id_merchandiser' })
	public fkMerchandiserLocation?: LocationEntity;

	@ManyToOne(() => FileEntity)
	@JoinColumn({ name: 'fk_file__id' })
	public fkFile?: FileEntity;
}
